<template>
  <StyledDiv
    class="page"
    justifyContent="left"
    alignItems="flex-start"
    width="75%"
    margin="80px 0"
  >
    <div class="h1"> Analyze a Language and Create the Language Database </div> <br/>
    <div class="body">
        To create a language database, files or web-address URLs containing text of a language are imported into the Literacy Loom system. The text is analyzed to ensure all symbols in a string of characters belong to the language. New words that are identified are displayed to allow users to indicate whether any should be deleted.
        <br/><br/>
        As the language is analyzed, the frequencies of use of symbols (i.e., letters, punctuation), words, and 3-word phrases are tracked. These will be used to make recommendations for letters and words when writing phonics-based decodable stories in the story-writing section of Literacy Loom.
    </div>
    <div class="imageBox" >
      <StyledImage
        :src="ph2supp1"
        maxWidth="800px"
      />
    </div> <br/>
    <br/><br/>
    
    <div class="h1"> Selecting Letters/Sounds to be Used in Writing Decodable Stories </div> <br/>
    <div class="body">
In a series of decodable stories, each story should introduce at most a few new sounds, depending on whether the learner is a beginning reader, a student who is learning to speak in the language in which they are also learning to read (e.g., an English language learner), a learner who already reads in another language, a student with special needs, etc.
        <br/><br/>
In a language that is phonetically regular, letters are displayed in order of frequency in the language, from highest to lowest. Research suggests that the most common sounds (phonemes) should be taught first, with consideration being given to common words used in the language. In English, research-based recommendations take letter frequency as well as consonant-vowel-consonant (CVC) words using short vowel sounds into account, as the common core standards suggest teaching short vowel sounds first. Additional supports will be added to provide stronger guidance for sound/symbol selection based on the type of learner targeted with decodable stories.
        <br/><br/>
The letters in black indicate sounds that have been taught in previous stories. The letters in blue are the sounds selected to be taught in the current story. Note that in this screen capture, more sounds have been taught in the first 3 stories than would be recommended for a beginning reader.
        <br/><br/>
The gold box indicates the new sounds and words used in the current story. Those new sounds and words are generated automatically as the story is composed. As such, even though the letters l, t, and T are selected to be taught in this story, they are not included as new sounds until words are used in the story that include these new sounds. 
        <br/><br/>
 As sounds are selected, the columns below the sound selector show (a) all words of Max Word Length or less (adjustable) that were available with previously selected sounds and (b) all additional words of Max Word Length or less available with new sounds.  These lists are currently sortable alphabetically or by frequency of use. 
    </div>
    <br /><br />
    <div class="h2">Not Shown Here:</div><br />
    <div class="body">
      In many languages, sounds (phonemes) are represented by more than one letter or symbol. For example, in Spanish, the letter combinations ch, ll, and rr represent a single sound that is different than the individual letters: c, h, l, or r. Literacy Loom allows users to indicate which letter combinations make single sounds, and those combinations are also displayed in frequency (or alphabetic) order along with single letter sounds. However, those double letter combinations are not included in this screen capture. Additionally, Spanish and many other languages include diacritical marks such as accents that change the sound of the symbol. We are still investigating through review of the literature, customer discovery and prototype beta-testing the best method for including those on the sound selection screen.
    </div>
    <div class="imageBox" >
      <StyledImage
        :src="ph2supp2"
        maxWidth="800px"
      />
    </div> <br/>
    <br/><br/>

      <div class="h1"> Writing Decodable Stories </div> <br/>
      <div class="body">
        The Literacy Loom SaaS guides users in writing a series of decodable stories for teaching phonics-based beginning literacy skills. The type of learner (beginning reader, a student who is learning to speak in the language in which they are also learning to read, a learner who already reads in another language, a student with special needs, etc.) is selected prior to story-writing so prompts can appropriately guide storywriters with evidence-based recommendations for that target group.
          <br /><br />
        As a storywriter types a story, the list of new words available (based on new sounds taught in the current book in combination with previously taught sounds) and 2-word phrases that could come next – based on the language analysis – are displayed, limited to the sounds that have been taught in this story or a prior story. If a symbol is entered that has not yet been taught, the storywriter is asked to confirm. For example, in English, we might want to teach the single letter words “I” and “a” or high frequency words (i.e., the, this, of, is, to) even if not all sounds in the word have previously been taught. Common lists of high frequency words (i.e., Fry, Dolch, Fountas & Pinnell, Zeno or other lists in English; words selected by high frequency in other languages) will be included in the Literacy Loom SaaS. 
          <br /><br />
        On the left side of the screen, cumulative sound and word counts are displayed to remind storywriters to provide sufficient within-story practice opportunities for learners. Once these numbers hit specific threshold values (i.e., default values: practiced in at least 5 stories and at least 15 times), sounds or words are removed from the display. 
          <br /><br />
        Many page templates are available to provide variety. Several pages are automatically generated by the system:
        <ul class="list">
          <li>The Cover (C) page that includes the story title, series and set information, and a graphic.</li>
          <li>An Acknowledgements (Ac) page that can include any information and can replicate an acknowledgements page from prior stories.</li>
          <li>Pages to provide practice for sounds (PS) and words (PW) taught in prior stories. These are automatically generated using evidence-based default values for learner type. These default settings can be adjusted, but this is not recommended unless users have strong knowledge of the reading research. </li>
          <li>Pages to provide practice for new sounds (S) and new words (W) included in the current story. These are updated as new pages are added to the story. Additionally, games will be included in phase 2 that will provide additional distributed sound and word practice with error detection and cumulative review for both new and previously taught sounds and words. </li>
          <li>Pages at the end of a story to provide practice for new sounds and words presented in the next story.</li>
          <li>Assessment activities and games to ensure mastery learning.</li>
        </ul>
      </div>
    <div class="imageBox" >
      <StyledImage
        :src="ph2supp3"
        maxWidth="800px"
      />
    </div> <br/>
    
  </StyledDiv>  
</template>

<script>
import ph2supp1 from "../assets/images/ph2supp1.png";
import ph2supp2 from "../assets/images/ph2supp2.png";
import ph2supp3 from "../assets/images/ph2supp3.png";

import { StyledDiv, StyledImage } from "../styled";
import { children } from "../helpers/children";
import StyleCompiler from "../styled/StyleCompiler";
export default {
  components: {
    StyledDiv,
    StyledImage
  },
  data() {
    return {
      children: children,
      ph2supp1: ph2supp1,
      ph2supp2: ph2supp2,
      ph2supp3: ph2supp3
    };
  },
  methods: {
    styled(e) {
      return StyleCompiler(e);
    }
  }
};
</script>

<style>
.page {
  font-family: 'GothamMedium';
}

.h1 {
  font-size: 1.5em;
  color: #51ACE4;
}

.h2 {
  font-size: 1.25em;
  color: #51ACE4;
  font-style: italic;
}

.body {
  font-size: 1.0em;
  font-family: 'Gotham'
}

li {
  margin-bottom: 1em
}

.imageBox {
  width: 100%;
}
/* .heading {
  fontFamily="styled('fontFamily-GothamMedium')"
  color="styled('pdfPink')"
  fontSize="styled('fontSize-mediumLarge')"
  mobileFontSize="styled('fontSize-medium')"
} */
@media (max-width: 1000px) {
  /* .graphicWrap {
    width: 100%;
    padding: 0 15px;
  } */
}
@media (max-width: 800px) {
  /* .childrenWrap {
    justify-content: center;
  } */
}
@media (max-width: 420px) {
  /* .childrenWrap {
    justify-content: flex-end;
  } */
}
</style>
